import React, { useState, useEffect, useCallback } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'react-toastify';
import AddRepositoryModal from '../../features/repositories/AddRepositoryModal';
import EditRepositoryModal from '../../features/repositories/EditRepositoryModal';
import ConfirmationModal from '../../components/common/ConfirmationModal/ConfirmationModal';
import LoadingBackdrop from '../../components/common/LoadingBackdrop/LoadingBackdrop';
import SearchBox from '../../components/common/SearchBox/SearchBox';
import Pagination from '../../components/common/Pagination/Pagination';
import RepositoryRow from '../../features/repositories/RepositoryRow';
import { repositoryService } from '../../services/apiService';
import useDebounce from '../../hooks/useDebounce';
import '../../components/common/EntityList/EntityList.css';
import '../../components/common/EntityRow/EntityRow.css';
import '../../features/repositories/RepositoryRow.css';

function Repositories() {
    const [repositories, setRepositories] = useState([]);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [selectedRepository, setSelectedRepository] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);

    const KEY = 'repoState';
    const parsedData = JSON.parse(sessionStorage.getItem(KEY) || '{}');

    // Pagination and search state
    const [currentPage, setCurrentPage] = useState(parsedData?.page || 1);
    const [totalItems, setTotalItems] = useState(0);
    const [searchQuery, setSearchQuery] = useState(parsedData?.search || '');
    const itemsPerPage = 6;
    const debouncedSearch = useDebounce(searchQuery, 500);

    useEffect(() => {
        sessionStorage.setItem(KEY, JSON.stringify({ page: currentPage, search: searchQuery }));
    }, [currentPage, searchQuery]);

    // Fetch repositories from API
    const fetchRepositories = useCallback(async () => {
        try {
            await new Promise((resolve) => { setTimeout(resolve, 300) });

            const response = await repositoryService.getAllRepos({
                page: currentPage,
                limit: itemsPerPage,
                search: debouncedSearch
            });

            setRepositories(response.items || []);
            setTotalItems(response.total || 0);

            if (currentPage > 1 && response.items.length === 0) {
                setCurrentPage(prev => prev - 1);
            }

        } catch (error) {
            toast.error(error.message || 'Failed to fetch repositories');
            console.error('Fetch repositories error:', error);
            setRepositories([]);
            setTotalItems(0);
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, debouncedSearch]);

    // Fetch on mount and when page/search changes
    useEffect(() => {
        setIsLoading(true);
        fetchRepositories();
    }, [fetchRepositories]);

    const handleAddRepository = () => setIsAddModalOpen(true);
    const handleCloseAddModal = () => setIsAddModalOpen(false);

    const handleEditRepository = (repository) => {
        setSelectedRepository(repository);
        setIsEditModalOpen(true);
    };

    const handleCloseEditModal = () => {
        setIsEditModalOpen(false);
        setSelectedRepository(null);
    };

    const handleDeleteRepository = (id) => {
        setPendingAction({ type: 'delete', id });
        setIsConfirmModalOpen(true);
    };

    const handleConfirm = async () => {
        if (!pendingAction) return;
        setIsLoading(true);

        try {
            if (pendingAction.type === 'delete') {
                const response = await repositoryService.deleteRepo(pendingAction.id);
                if (response) toast.success('Repository deleted successfully');
            }

            await fetchRepositories();
        } catch (error) {
            const fallback = 'Failed to delete repository. Please try again.';
            toast.error(error.message || fallback);
            console.error('Confirm action error:', error);
        } finally {
            setIsLoading(false);
            setIsConfirmModalOpen(false);
            setPendingAction(null);
        }
    };

    const handleCancelConfirm = () => {
        setIsConfirmModalOpen(false);
        setPendingAction(null);
    };

    const handlePageChange = (newPage) => setCurrentPage(newPage);

    const handleSearchChange = (query) => {
        setSearchQuery(query);
        setCurrentPage(1);
    };

    // Refresh after successful add
    const handleRepositoryAdded = async () => {
        if (currentPage === 1) {
            await fetchRepositories();
        } else {
            setCurrentPage(1);
        }
    };

    const handleRepositoryUpdated = () => fetchRepositories();

    const confirmMessage = 'Do you want to delete this repository?';

    return (
        <div className="page-content">
            {/* Loading Backdrop */}
            <LoadingBackdrop isLoading={isLoading} />

            <div className="page-header">
                <div className="page-title-section">
                    <h1 className="page-title">Repositories</h1>
                    <p className="page-subtitle">Browse and manage all repositories</p>
                </div>
                <button className="add-connection-btn" onClick={handleAddRepository}>
                    <Plus style={{ width: "1.042vw", height: "1.042vw" }} />
                    Add Repository
                </button>
            </div>

            {/* Repository List */}
            <div className="connections-list">
                <div className="connections-header">
                    <h2 className="connections-title">Repositories</h2>
                    <SearchBox
                        value={searchQuery}
                        onChange={handleSearchChange}
                        placeholder="Search repository connections"
                        width="16.667vw"
                        iconSize="0.833vw"
                    />
                </div>

                <div className="connections-rows">
                    {repositories.length > 0 ? (
                        repositories.map((repo) => (
                            <RepositoryRow
                                key={repo.id}
                                item={repo}
                                onEdit={handleEditRepository}
                                onDelete={handleDeleteRepository}
                            />
                        ))
                    ) : (
                        <div className="no-results">
                            <p>
                                No repositories found
                                {searchQuery ? ` for "${searchQuery}"` : ""}
                            </p>
                        </div>
                    )}
                </div>

                {totalItems > 0 && (
                    <Pagination
                        currentPage={currentPage}
                        totalItems={totalItems}
                        itemsPerPage={itemsPerPage}
                        onPageChange={handlePageChange}
                        itemName="repositories"
                    />
                )}
            </div>

            {/* Add Repository Modal */}
            {isAddModalOpen && (
                <AddRepositoryModal
                    isOpen={isAddModalOpen}
                    onClose={handleCloseAddModal}
                    onSuccess={handleRepositoryAdded}
                    setIsLoading={setIsLoading}
                />
            )}

            {/* Edit Repository Modal */}
            {isEditModalOpen && selectedRepository && (
                <EditRepositoryModal
                    isOpen={isEditModalOpen}
                    onClose={handleCloseEditModal}
                    onSuccess={handleRepositoryUpdated}
                    repoData={selectedRepository}
                    setIsLoading={setIsLoading}
                />
            )}

            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={isConfirmModalOpen}
                onClose={handleCancelConfirm}
                onConfirm={handleConfirm}
                message={confirmMessage}
                title="Confirm Action"
                confirmText="Confirm"
                cancelText="Cancel"
                isLoading={isLoading}
            />
        </div>
    );
}

export default Repositories;